import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../icons/Icon";
import { cn } from "@/lib/styles.utils";
import Label from "../label/Label";

export type SelectOption = { label: string; value: string; disabled?: boolean };

interface CustomSelectProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  titleInPanel?: string;
  options: SelectOption[];
  value?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  error?: boolean;
  id: string;
  children?: React.ReactNode;
}

const InputSelect = ({
  label,
  placeholder,
  required,
  titleInPanel,
  options,
  value = null,
  onChange,
  disabled,
  name,
  error,
  id,
  children,
}: CustomSelectProps) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const [internalValue, setInternalValue] = useState<string | null>(value);

  const listboxId = `${id}-listbox`;

  useEffect(() => setInternalValue(value ?? null), [value]);

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === internalValue),
    [options, internalValue],
  );

  const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : "";

  useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      if (!buttonRef.current && !listRef.current) return;
      const target = ev.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
      setHighlight(-1);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const commitChange = (newVal: string | null) => {
    setInternalValue(newVal);
    onChange?.(newVal);
    setOpen(false);
  };

  const moveHighlight = (dir: 1 | -1) => {
    if (!open) setOpen(true);
    const len = options.length;
    let next = highlight;
    for (let i = 0; i < len; i++) {
      next = (next + dir + len) % len;
      if (!options[next].disabled) break;
    }
    setHighlight(next);

    requestAnimationFrame(() => {
      const el = document.getElementById(`${id}-option-${next}`);
      el?.scrollIntoView?.({ block: "nearest" });
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        break;
      case "Home":
        e.preventDefault();
        setHighlight(0);
        break;
      case "End":
        e.preventDefault();
        setHighlight(options.length - 1);
        break;
      case "Enter":
      case " ":
        if (open && highlight >= 0) {
          e.preventDefault();
          commitChange(options[highlight].value);
        } else if (!open) {
          e.preventDefault();
          setOpen(true);
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          setOpen(false);
          setHighlight(-1);
        }
        break;
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 w-full",
        !label
          ? "h-[41px] min-h-[41px] max-h-[41px]"
          : "h-[65px] min-h-[65px] max-h-[65px]",
      )}
    >
      {label && <Label label={label} id={id} required={required} />}
      <div className="relative pb-2">
        <button
          ref={buttonRef}
          id={id}
          name={name}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onKeyDown={onKeyDown}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            `group w-full rounded-xs border border-[#E4E4E4] bg-white px-3 text-left text-[14px] transition-shadow focus:outline-none disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:text-[#9E9E9E] py-[13px]`,
            {
              "ring-1 ring-[#D7192D]": error,
              "ring-1 ring-[#B1B1B1]": open,
              "ring-1 ring-transparent": !error && !open,
            },
          )}
        >
          <div className="flex items-center gap-2">
            {children}
            {selectedIndex >= 0 ? (
              <span className="truncate text-[#3D3D3D]">{selectedLabel}</span>
            ) : (
              <span className="truncate text-[#9E9E9E]">{placeholder}</span>
            )}
            <Icon
              name={open ? "fillArrowUp" : "fillArrowDown"}
              className="text-[#D7192D] ml-auto size-4 shrink-0  transition-transform"
            />
          </div>
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xs border border-[#EDEDED] bg-white shadow-md ">
            {titleInPanel && (
              <div className="px-3 py-2 text-[14px] text-[#7A7A7A] border-b">
                {titleInPanel}
              </div>
            )}
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-labelledby={id}
              className="max-h-64 overflow-auto custom-scrollbar"
            >
              {options.map((opt, idx) => {
                const selected = idx === selectedIndex;
                const highlighted = idx === highlight;
                const base =
                  "w-full px-3 py-3 text-[14px] text-[#3D3D3D] transition-colors";
                const state = opt.disabled
                  ? "opacity-50 cursor-not-allowed"
                  : highlighted
                    ? "bg-[#FDE7EA]"
                    : "hover:bg-[#F7F7F7]";
                const border = "border-t first:border-t-0 border-[#EFEFEF]";
                return (
                  <li
                    id={`${id}-option-${idx}`}
                    key={opt.value}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={opt.disabled || undefined}
                    tabIndex={-1}
                    onMouseEnter={() => !opt.disabled && setHighlight(idx)}
                    onMouseLeave={() => setHighlight(-1)}
                    onClick={() => !opt.disabled && commitChange(opt.value)}
                    className={cn(
                      { "bg-[#FDE7EA]": internalValue === opt.value },
                      base,
                      state,
                      border,
                      "cursor-pointer select-none",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate">{opt.label}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputSelect;
